import { useState } from 'react';
import {
  Box, Stack, Typography, Button, TextField, Alert, Paper,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SymptomReading } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import ScorePicker, { type ScoreDirection } from './ScorePicker';

type Key = 'breathlessness' | 'pain' | 'sleep' | 'appetite' | 'mood' | 'appearance';

const FIELDS: Array<{ key: Key; label: string; hint: string; direction: ScoreDirection }> = [
  { key: 'breathlessness', label: 'Breathlessness',   hint: '0 easy · 10 severe',       direction: 'rising-bad'  },
  { key: 'pain',           label: 'Pain',             hint: '0 none · 10 worst',         direction: 'rising-bad'  },
  { key: 'sleep',          label: 'Sleep quality',    hint: '0 poor · 10 great',         direction: 'rising-good' },
  { key: 'appetite',       label: 'Appetite',         hint: '0 none · 10 normal',        direction: 'rising-good' },
  { key: 'mood',           label: 'Mood',             hint: '0 low · 10 bright',         direction: 'rising-good' },
  { key: 'appearance',     label: 'How does she look', hint: '0 withdrawn · 10 bright',  direction: 'rising-good' },
];

type State = Record<Key, number | null>;

const EMPTY: State = {
  breathlessness: null,
  pain: null,
  sleep: null,
  appetite: null,
  mood: null,
  appearance: null,
};

export default function SymptomSliders() {
  const [values, setValues] = useState<State>(EMPTY);
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: State & { notes: string | null }) =>
      api.post<SymptomReading>('/api/symptoms', payload),
    onSuccess: () => {
      setValues(EMPTY);
      setNotes('');
      qc.invalidateQueries({ queryKey: ['symptoms'] });
    },
  });

  const onSubmit = () => {
    mutation.mutate({
      ...values,
      notes: notes.trim() || null,
    });
  };

  const hasAny = Object.values(values).some((v) => v !== null);
  const pickedCount = Object.values(values).filter((v) => v !== null).length;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}
      >
        <Typography variant="h6">Log symptoms</Typography>
        <Typography variant="caption" color="text.secondary">
          {pickedCount === 0 ? 'tap a number for each' : `${pickedCount}/6 set`}
        </Typography>
      </Stack>
      <Stack spacing={2.5}>
        {FIELDS.map((f) => (
          <ScorePicker
            key={f.key}
            label={f.label}
            hint={f.hint}
            value={values[f.key]}
            onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
            direction={f.direction}
          />
        ))}
        <TextField
          label="Notes (optional)"
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
        <Box>
          <Button
            variant="contained"
            size="large"
            onClick={onSubmit}
            disabled={mutation.isPending || !hasAny}
          >
            {mutation.isPending ? 'Saving…' : 'Save reading'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
